// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useAuth } from "@/lib/auth";
import CenteredCardSkeleton from "@/components/auth/skeletons/CenteredCardSkeleton";
import { useGetInvitationByToken, usePostAcceptInvite } from "@/hooks/useInvitations";
import { INVITE_RECOVERY_HEADLINES } from "@/constant/auth.constant";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { FormInput } from "@/components/forms/FormControls";
import { acceptInviteSchema } from "@/schemas/auth";
import { InvitationKind } from "@/enum/common.enum";
import { PUBLIC_ROUTES } from "@/routes/url";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { installSession } = useAuth() || {};

  const { data: inv, error: invError, isLoading } = useGetInvitationByToken(token);
  const { mutate: acceptInvite, isPending: busy } = usePostAcceptInvite();
  const existingUser = Boolean(inv?.user_exists);
  const form = useForm({
    resolver: zodResolver(acceptInviteSchema(existingUser)),
    defaultValues: { name: "", password: "", email: "" },
  });

  useEffect(() => { form.setValue("email", inv?.email || ""); }, [form, inv?.email]);

  let err = null;
  if (invError) {
    const d = invError?.response?.data?.detail;
    err = typeof d === "object" && d?.code ? d : { code: "invalid", message: typeof d === "string" ? d : "This invitation link isn't valid." };
  }

  const accept = (values) => {
    acceptInvite(
      { token, payload: { name: values.name, password: values.password } },
      {
        onSuccess: async (res) => {
          await installSession(res.data.token, res.data.user);
          navigate("/overview", { replace: true });
        },
      }
    );
  };

  if (err) {
    const headline = INVITE_RECOVERY_HEADLINES[err.code] || err.message || "This link isn't valid.";
    const inviter = err.inviter;
    const context = err.deal_address || err.organization_name;
    const subject = encodeURIComponent(context ? `New invite link for ${context}` : "New invite link");
    const firstName = inviter?.name ? inviter.name.split(" ")[0] : "";
    const body = encodeURIComponent(
      `Hi${firstName ? " " + firstName : ""},\n\n` +
      `The invitation link you sent me${context ? " for " + context : ""} isn't working anymore — could you send a fresh one when you get a chance?\n\n` +
      `Thanks!`
    );
    return (
      <div className="min-h-screen bg-linen flex items-center justify-center p-8">
        <div className="text-center max-w-md" data-testid="accept-invite-recovery">
          <div className="overline mb-4">Invitation</div>
          <h1 className="font-display text-4xl tracking-tight">{headline}</h1>
          {context && <p className="text-stone mt-3 text-sm">For <span className="text-ink">{context}</span>.</p>}
          <div className="mt-8 border border-edge bg-white p-6 text-left">
            <div className="overline mb-2">What to do next</div>
            {inviter?.email ? (
              <>
                <p className="text-sm text-ink leading-relaxed">
                  Ask <span className="font-medium">{inviter.name}</span> to send you a fresh link.
                </p>
                <a
                  href={`mailto:${inviter.email}?subject=${subject}&body=${body}`}
                  className="inline-flex items-center gap-2 mt-4 bg-moss text-linen px-4 py-2.5 text-sm hover:bg-mossDark"
                  data-testid="accept-invite-mailto"
                >
                  Email {firstName || inviter.name}
                </a>
                <p className="text-xs text-stone mt-3">Or reach them directly at <span className="text-ink">{inviter.email}</span>.</p>
              </>
            ) : (
              <p className="text-sm text-stone">Ask the person who sent it to issue a new link.</p>
            )}
          </div>
        </div>
      </div>
    );
  }
  if (isLoading || !inv) return <CenteredCardSkeleton />;

  return (
    <div className="min-h-screen bg-linen flex items-center justify-center p-6">
      <Form {...form}>
      <form onSubmit={form.handleSubmit(accept)} className="bg-white border border-edge max-w-md w-full p-10" data-testid="accept-invite-form" noValidate>
        <div className="overline mb-3" data-testid="invite-kind">{inv.kind === InvitationKind.ORGANIZATION ? `Joining ${inv.organization_name}` : `Joining a deal`}</div>
        <h1 className="font-display text-3xl tracking-tight">
          {existingUser ? "Welcome back." : "You’ve been invited."}
        </h1>
        <p className="text-stone text-sm mt-3">
          {inv.kind === InvitationKind.ORGANIZATION
            ? <>As <span className="text-ink">{inv.role}</span> at <span className="text-ink">{inv.organization_name}</span>.</>
            : <>As <span className="text-ink">{inv.participant_role}</span> on <span className="text-ink">{inv.deal_address}</span>.</>}
        </p>
        {existingUser && (
          <div className="mt-4 p-3 bg-bone border-l-2 border-moss text-xs text-stone" data-testid="invite-existing-user-note">
            An account already exists for <span className="text-ink">{inv.email}</span>. Sign in with your existing password to accept this invitation.
          </div>
        )}
        <div className="mt-8 flex flex-col gap-4">
          {!existingUser && (
            <FormInput name="name" label="Your name" autoComplete="name" data-testid="accept-name" required />
          )}
          <FormInput name="email" label="Email" type="email" disabled />
          <FormInput name="password" label={existingUser ? "Your password" : "Choose a password"} type="password" autoComplete={existingUser ? "current-password" : "new-password"} data-testid="accept-password" required />
        </div>
        <Button type="submit" variant="dashboardPrimary" loading={busy} className="mt-8 w-full" data-testid="accept-submit">
          {existingUser ? "Sign in & accept" : "Accept & sign in"}
        </Button>
      </form>
      </Form>
    </div>
  );
}
